import { useCallback, useMemo, useState } from 'react';

export type PropertiesPanelDock = 'right' | 'side';
export type WorkspaceLayoutMode = 'workspace' | 'drawer';
export type SectionRails = 'compact' | 'expanded';
export type ColorScheme = 'system' | 'light' | 'dark';
export type PanelKey = 'left' | 'right' | 'assets';
export type MeasurementUnit = 'm' | 'cm' | 'mm' | 'in';

export interface WorkspacePreferences {
    colorScheme: ColorScheme;
    layoutMode: WorkspaceLayoutMode;
    propertiesPanel: PropertiesPanelDock;
    sectionRails: SectionRails;
    measurementUnit: MeasurementUnit;
    collapsed: PanelKey[];
    sizes: Record<PanelKey, number>;
}

export const WORKSPACE_PREFERENCES_KEY = 'zakeke.sceneEditor.workspace';

// I default di fabbrica sono quelli del prototipo: le proprietà accanto alla lista, e i
// rail che scrivono i nomi. Non sono una preferenza di gusto — sono la schermata che
// chiunque apra per la prima volta deve vedere, ed è quella su cui il disegno è stato
// giudicato. L'altro attracco resta a un clic, in Settings → Interface.
const DEFAULT_PREFERENCES: WorkspacePreferences = {
    colorScheme: 'system',
    layoutMode: 'workspace',
    propertiesPanel: 'side',
    sectionRails: 'expanded',
    measurementUnit: 'm',
    collapsed: [],
    sizes: { left: 332, right: 332, assets: 284 }
};

const MAX_PANEL_SIZE = 560;
const RAIL_WIDTHS: Record<SectionRails, number> = { compact: 43, expanded: 70 };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getPanelMinimum = (panel: PanelKey, sectionRails: SectionRails) => {
    if (panel === 'assets') return 43;
    return 284 + RAIL_WIDTHS[sectionRails];
};

const normalizePreferences = (value: Partial<WorkspacePreferences>): WorkspacePreferences => {
    // Il confronto è con TUTTI i valori ammessi, non solo con quello diverso dal default:
    // scritto all'incontrario, cambiare il default riscriverebbe la scelta di chi aveva
    // preso l'altra.
    const sectionRails = value.sectionRails === 'compact' || value.sectionRails === 'expanded' ? value.sectionRails : DEFAULT_PREFERENCES.sectionRails;
    const collapsed = Array.isArray(value.collapsed) ? value.collapsed.filter((panel): panel is PanelKey => ['left', 'right', 'assets'].includes(panel)) : [];
    const incomingSizes: Partial<Record<PanelKey, number>> = value.sizes ?? {};

    return {
        colorScheme: value.colorScheme === 'light' || value.colorScheme === 'dark' ? value.colorScheme : DEFAULT_PREFERENCES.colorScheme,
        // L'Inspector a cassetto non è una scelta offerta: lo step 5 lo vuole colonna, e nel
        // prototipo il cassetto non esiste. Chi se l'era salvato torna qui, o resterebbe
        // chiuso in una disposizione senza più il comando per uscirne.
        layoutMode: 'workspace',
        propertiesPanel: value.propertiesPanel === 'side' || value.propertiesPanel === 'right' ? value.propertiesPanel : DEFAULT_PREFERENCES.propertiesPanel,
        sectionRails,
        measurementUnit: ['m', 'cm', 'mm', 'in'].includes(value.measurementUnit ?? '') ? (value.measurementUnit as MeasurementUnit) : 'm',
        collapsed,
        sizes: {
            left: clamp(Number(incomingSizes.left) || DEFAULT_PREFERENCES.sizes.left, getPanelMinimum('left', sectionRails), MAX_PANEL_SIZE),
            right: clamp(Number(incomingSizes.right) || DEFAULT_PREFERENCES.sizes.right, getPanelMinimum('right', sectionRails), MAX_PANEL_SIZE),
            assets: clamp(Number(incomingSizes.assets) || DEFAULT_PREFERENCES.sizes.assets, getPanelMinimum('assets', sectionRails), MAX_PANEL_SIZE)
        }
    };
};

const readPreferences = (): WorkspacePreferences => {
    if (typeof window === 'undefined') return DEFAULT_PREFERENCES;

    try {
        const stored = window.localStorage.getItem(WORKSPACE_PREFERENCES_KEY);
        return stored ? normalizePreferences(JSON.parse(stored)) : DEFAULT_PREFERENCES;
    } catch {
        return DEFAULT_PREFERENCES;
    }
};

export const useWorkspacePreferences = () => {
    // Sorgente provvisoria: le quattro preferenze semantiche **e la geometria delle colonne**
    // arriveranno da un fragment in appStateQuery, alimentato dalla tabella dell’Interface Manager.
    //
    // LA GEOMETRIA VA SUL SERVER PER LIVELLO E IN FRAZIONE (utente, 2026-08-26): si salva «quanto è
    // larga la seconda colonna» — panel1, panel2, panel3 — e non «quanto è largo l'Inspector», e il
    // numero è la larghezza divisa per quella del workspace, non un conto di pixel che su un altro
    // schermo non vuol dire niente. Il contratto sta in docs/preferenze-interfaccia-microservizio.md.
    //
    // `collapsed` resta locale: quali pannelli sono chiusi è uno stato del momento, come la sidebar.
    const [preferences, setPreferences] = useState<WorkspacePreferences>(readPreferences);

    const persist = useCallback((next: WorkspacePreferences) => {
        setPreferences(next);
        if (typeof window !== 'undefined') window.localStorage.setItem(WORKSPACE_PREFERENCES_KEY, JSON.stringify(next));
    }, []);

    const updatePreferences = useCallback((updates: Partial<WorkspacePreferences>) => {
        setPreferences((current) => {
            const next = normalizePreferences({ ...current, ...updates, sizes: { ...current.sizes, ...updates.sizes } });
            if (typeof window !== 'undefined') window.localStorage.setItem(WORKSPACE_PREFERENCES_KEY, JSON.stringify(next));
            return next;
        });
    }, []);

    const setPreference = useCallback(
        <K extends keyof WorkspacePreferences>(key: K, value: WorkspacePreferences[K]) => {
            updatePreferences({ [key]: value } as Partial<WorkspacePreferences>);
        },
        [updatePreferences]
    );

    const setPanelSize = useCallback(
        (panel: PanelKey, size: number) => {
            updatePreferences({ sizes: { ...preferences.sizes, [panel]: size } });
        },
        [preferences.sizes, updatePreferences]
    );

    const toggleCollapsed = useCallback(
        (panel: PanelKey) => {
            const collapsed = preferences.collapsed.includes(panel)
                ? preferences.collapsed.filter((currentPanel) => currentPanel !== panel)
                : [...preferences.collapsed, panel];
            updatePreferences({ collapsed });
        },
        [preferences.collapsed, updatePreferences]
    );

    return useMemo(
        () => ({ preferences, setPreference, setPanelSize, toggleCollapsed, updatePreferences, persist }),
        [preferences, persist, setPanelSize, setPreference, toggleCollapsed, updatePreferences]
    );
};

export const getWorkspacePanelMinimum = (panel: PanelKey, sectionRails: SectionRails) => getPanelMinimum(panel, sectionRails);
export const getWorkspaceRailWidth = (sectionRails: SectionRails) => RAIL_WIDTHS[sectionRails];
export const WORKSPACE_MAX_PANEL_SIZE = MAX_PANEL_SIZE;
